import React, { useEffect, useState } from "react";
import { manualApi } from "@/lib/api";
import { openWixStore } from "@/lib/wixLinks";
import Loading from "@/components/app/Loading";
import { Lock, BookOpen } from "lucide-react";

// The Manual is a mixed-content product: authored reading blocks + reference tools +
// workflow phases + worksheet templates. For the initial launch we render the reading
// blocks (which have authored copy) and list the tools/phases/templates as
// section previews. Detail views for individual tools/templates are P1.

function ReadingBlock({ block }) {
  if (!block) return null;
  const { type, heading, text, items } = block;
  if (type === "heading") {
    return <h3 className="pa-serif text-lg mt-3 mb-2" style={{ color: "var(--pa-gold-light)" }}>{heading || text}</h3>;
  }
  if (type === "list") {
    return (
      <ul className="text-sm list-disc list-inside space-y-1" style={{ color: "var(--pa-text-primary)" }}>
        {(items || []).map((i, k) => <li key={k}>{i}</li>)}
      </ul>
    );
  }
  return <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{text || ""}</p>;
}

export default function Manual() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    manualApi.getManual()
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="pa-card p-6 text-center">
        <Lock size={22} color="var(--pa-gold)" className="mx-auto mb-3" />
        <h2 className="pa-serif text-xl mb-2" style={{ color: "var(--pa-text-primary)" }}>Manual locked</h2>
        <p className="text-sm mb-4" style={{ color: "var(--pa-text-secondary)" }}>{error.message}</p>
        <button className="pa-btn-gold inline-block" onClick={openWixStore}>Unlock Manual</button>
      </div>
    );
  }

  if (!data) return null;

  const card = data.archetype_card;
  const reading = data.reading || [];
  const tools = data.quick_ref_tools || [];
  const phases = data.workflow_phases || [];
  const templates = data.worksheet_templates || [];

  return (
    <div className="space-y-6">
      <header>
        <div className="inline-flex items-center gap-2 mb-1">
          <BookOpen size={16} color="var(--pa-gold)" />
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--pa-gold)" }}>Manual</span>
        </div>
        <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>Field Companion</h1>
        {data.placeholder_label && (
          <p className="text-xs mt-2 italic" style={{ color: "var(--pa-text-muted)" }}>{data.placeholder_label}</p>
        )}
      </header>

      {card && (
        <section className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-xl mb-1" style={{ color: "var(--pa-gold)" }}>{card.archetype}</h2>
          {card.tagline && <p className="text-sm italic mb-3" style={{ color: "var(--pa-text-secondary)" }}>{card.tagline}</p>}
          <div className="grid gap-3 md:grid-cols-2 text-sm" style={{ color: "var(--pa-text-primary)" }}>
            {card.key_strength && <div><span className="text-xs uppercase" style={{ color: "var(--pa-gold-light)" }}>Key strength</span><p>{card.key_strength}</p></div>}
            {card.best_team_role && <div><span className="text-xs uppercase" style={{ color: "var(--pa-gold-light)" }}>Best team role</span><p>{card.best_team_role}</p></div>}
            {card.strongest_synergy && <div><span className="text-xs uppercase" style={{ color: "var(--pa-gold-light)" }}>Strongest synergy</span><p>{card.strongest_synergy}</p></div>}
          </div>
        </section>
      )}

      {reading.map((sec) => (
        <section key={sec.key} className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-xl mb-1" style={{ color: "var(--pa-gold)" }}>{sec.title}</h2>
          {sec.subtitle && <p className="text-sm italic mb-3" style={{ color: "var(--pa-text-secondary)" }}>{sec.subtitle}</p>}
          <div className="space-y-3">
            {(sec.blocks || []).map((b, i) => <ReadingBlock key={i} block={b} />)}
          </div>
        </section>
      ))}

      {phases.length > 0 && (
        <section className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-xl mb-3" style={{ color: "var(--pa-gold)" }}>Workflow Phases</h2>
          <div className="space-y-4">
            {phases.map((p) => (
              <div key={p.key}>
                <h3 className="pa-serif text-base mb-2" style={{ color: "var(--pa-text-primary)" }}>{p.title}</h3>
                <ol className="text-sm list-decimal list-inside space-y-1" style={{ color: "var(--pa-text-primary)" }}>
                  {(p.steps || []).map((s, i) => <li key={i}>{typeof s === "string" ? s : (s.text || s.title)}</li>)}
                </ol>
              </div>
            ))}
          </div>
        </section>
      )}

      {tools.length > 0 && (
        <section className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-xl mb-3" style={{ color: "var(--pa-gold)" }}>Quick Reference Tools</h2>
          <div className="space-y-4">
            {tools.map((t) => (
              <div key={t.key}>
                <h3 className="pa-serif text-base mb-2" style={{ color: "var(--pa-text-primary)" }}>{t.title}</h3>
                <ul className="text-sm list-disc list-inside space-y-1" style={{ color: "var(--pa-text-primary)" }}>
                  {(t.items || []).map((it, i) => <li key={i}>{typeof it === "string" ? it : (it.text || it.title)}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {templates.length > 0 && (
        <section className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-xl mb-3" style={{ color: "var(--pa-gold)" }}>Worksheet Templates</h2>
          <ul className="text-sm space-y-2" style={{ color: "var(--pa-text-primary)" }}>
            {templates.map((t) => (
              <li key={t.key} className="flex items-start gap-2">
                <span className="text-xs uppercase mt-0.5" style={{ color: "var(--pa-gold-light)" }}>{t.phase || ""}</span>
                <span>{t.title}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
